import { useState, useEffect } from 'react';
import { View, Modal, Alert, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';
import type { TrustedContact } from '@/services/safety';
import type { Collaborator } from '@/services/trips';

type LocationShareModalProps = {
  visible: boolean;
  onClose: () => void;
  onShare: (recipients: string[], tripId?: string, expiresInHours?: number) => Promise<void>;
  trips: Trip[];
  trustedContacts: TrustedContact[];
  isSharing: boolean;
};

export function LocationShareModal({
  visible,
  onClose,
  onShare,
  trips,
  trustedContacts,
  isSharing,
}: LocationShareModalProps) {
  const theme = useTheme();
  const [shareType, setShareType] = useState<'trip' | 'contacts' | 'both'>('trip');
  const [selectedTripId, setSelectedTripId] = useState<string>('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [expiresInHours, setExpiresInHours] = useState<string>('24');
  const [tripError, setTripError] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const [recipientError, setRecipientError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      // Reset form when modal closes
      setShareType('trip');
      setSelectedTripId('');
      setSelectedContactIds([]);
      setExpiresInHours('24');
      setTripError(null);
      setContactError(null);
      setRecipientError(null);
    }
  }, [visible]);

  const tripOptions: SelectOption[] = trips
    .filter((trip) => {
      const now = new Date();
      const endDate = new Date(trip.endDate);
      return endDate >= now; // Only show active/future trips
    })
    .map((trip) => ({
      value: trip.id,
      label: trip.name,
    }));

  const contactOptions: SelectOption[] = trustedContacts.map((contact) => ({
    value: contact.id,
    label: `${contact.name} (${contact.phoneNumber})`,
  }));

  const durationOptions: SelectOption[] = [
    { value: '1', label: '1 hour' },
    { value: '6', label: '6 hours' },
    { value: '12', label: '12 hours' },
    { value: '24', label: '24 hours' },
    { value: '48', label: '48 hours' },
    { value: '168', label: '1 week' },
  ];

  const handleShare = async () => {
    const recipients: string[] = [];

    if (shareType === 'trip' || shareType === 'both') {
      if (!selectedTripId) {
        setTripError('Choose a trip to share.');
        return;
      }
      const trip = trips.find((t) => t.id === selectedTripId);
      if (trip) {
        const hasCompanions = (trip.collaborators?.length || 0) > 0 || (trip.members?.length || 0) > 1;
        if (!hasCompanions && shareType === 'trip') {
          Alert.alert('No trip companions', 'Add collaborators to this trip or share with trusted contacts instead.');
          return;
        }
        // Add trip collaborators
        if (trip.collaborators) {
          recipients.push(...trip.collaborators.map((c) => c.userId));
        }
        if (trip.members) {
          recipients.push(...trip.members);
        }
      }
    }

    if (shareType === 'contacts' || shareType === 'both') {
      if (selectedContactIds.length === 0) {
        setContactError('Pick at least one trusted contact.');
        return;
      }
      // For contacts, prefer email but fall back to phone number for delivery
      selectedContactIds.forEach((contactId) => {
        const contact = trustedContacts.find((c) => c.id === contactId);
        if (contact?.email) {
          recipients.push(contact.email);
        } else if (contact?.phoneNumber) {
          recipients.push(contact.phoneNumber.replace(/[^\d+]/g, ''));
        }
      });
    }

    const uniqueRecipients = Array.from(new Set(recipients.filter(Boolean)));

    if (uniqueRecipients.length === 0) {
      setRecipientError('Pick at least one recipient to start sharing.');
      return;
    }

    try {
      setTripError(null);
      setContactError(null);
      setRecipientError(null);
      await onShare(
        uniqueRecipients,
        shareType !== 'contacts' ? selectedTripId : undefined,
        parseInt(expiresInHours, 10),
      );
    } catch (error) {
      Alert.alert('Unable to share', 'We couldn’t start location sharing right now. Please try again.');
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SelectFieldModalProvider>
      <SafeAreaView
        style={{
          flex: 1,
          backgroundColor: theme.colors.background,
        }}
        edges={['top', 'left', 'right', 'bottom']}
      >
        <View
          style={{
            flex: 1,
            padding: theme.spacing(2),
          }}
        >
        <SubHeading style={{ marginBottom: theme.spacing(2) }}>
          Share Live Location
        </SubHeading>

        <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
          <View style={{ gap: theme.spacing(3) }}>
            <View>
              <Body style={{ marginBottom: theme.spacing(1) }}>Share with:</Body>
              <View style={{ gap: theme.spacing(1) }}>
                <Button
                  label="Trip Companions"
                  variant={shareType === 'trip' ? 'primary' : 'secondary'}
                  onPress={() => setShareType('trip')}
                  style={{ marginBottom: theme.spacing(0.5) }}
                />
                <Button
                  label="Trusted Contacts"
                  variant={shareType === 'contacts' ? 'primary' : 'secondary'}
                  onPress={() => setShareType('contacts')}
                  style={{ marginBottom: theme.spacing(0.5) }}
                />
                <Button
                  label="Both"
                  variant={shareType === 'both' ? 'primary' : 'secondary'}
                  onPress={() => setShareType('both')}
                />
              </View>
            </View>

            {(shareType === 'trip' || shareType === 'both') && (
              <SelectField
                label="Select Trip"
                placeholder="Choose a trip"
                value={selectedTripId}
                options={tripOptions}
                onValueChange={(value) => {
                  setSelectedTripId(value);
                  if (tripError) setTripError(null);
                  if (recipientError) setRecipientError(null);
                }}
                errorText={tripError ?? undefined}
              />
            )}

            {(shareType === 'contacts' || shareType === 'both') && (
              <View>
                <Body style={{ marginBottom: theme.spacing(1) }}>Select Contacts:</Body>
                {contactOptions.length === 0 ? (
                  <Caption color={theme.colors.textSecondary}>
                    No trusted contacts. Add contacts first.
                  </Caption>
                ) : (
                  <View style={{ gap: theme.spacing(1) }}>
                    {contactOptions.map((option) => {
                      const isSelected = selectedContactIds.includes(option.value);
                      return (
                        <Button
                          key={option.value}
                          label={option.label}
                          variant={isSelected ? 'primary' : 'secondary'}
                          onPress={() => {
                            if (isSelected) {
                              setSelectedContactIds(selectedContactIds.filter((id) => id !== option.value));
                            } else {
                              setSelectedContactIds([...selectedContactIds, option.value]);
                            }
                            if (contactError) setContactError(null);
                            if (recipientError) setRecipientError(null);
                          }}
                        />
                      );
                    })}
                  </View>
                )}
                {contactError ? (
                  <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.75) }}>
                    {contactError}
                  </Caption>
                ) : null}
              </View>
            )}

            <SelectField
              label="Share Duration"
              placeholder="Select duration"
              value={expiresInHours}
              options={durationOptions}
              onValueChange={setExpiresInHours}
            />

            <View
              style={{
                padding: theme.spacing(2),
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radii.md,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
              }}
            >
              <Caption color={theme.colors.textSecondary}>
                Your location will be shared in real-time with selected recipients. You can stop sharing at any time.
              </Caption>
              {recipientError ? (
                <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(1) }}>
                  {recipientError}
                </Caption>
              ) : null}
            </View>
          </View>
        </ScrollView>

        <View style={{ gap: theme.spacing(1.5), marginTop: theme.spacing(2) }}>
          <Button
            label={isSharing ? 'Sharing...' : 'Start Sharing'}
            onPress={handleShare}
            disabled={isSharing}
          />
          <Button label="Cancel" variant="ghost" onPress={onClose} />
        </View>
        </View>
      </SafeAreaView>
      </SelectFieldModalProvider>
    </Modal>
  );
}





